/**
 * URLParameterHandler Utility
 * 
 * Handles URL parameter parsing and shared JSON loading functionality.
 * Detects share parameters in URLs and retrieves shared JSON data from the API.
 */

import { getApiEndpoint } from '../config/api-config.js';

export class URLParameterHandler {
    constructor(options = {}) {
        this.options = {
            onLoadStart: options.onLoadStart || (() => { }),
            onLoadSuccess: options.onLoadSuccess || (() => { }),
            onLoadError: options.onLoadError || (() => { }),
            onLoadComplete: options.onLoadComplete || (() => { }),
            ...options
        }

        // Set API endpoint after options are merged
        if (!this.options.apiEndpoint) {
            this.options.apiEndpoint = this.getApiEndpoint()
        }

        this.isLoading = false
    }

    /**
     * Get the appropriate API endpoint based on environment
     */
    getApiEndpoint() {
        // Allow override via options for testing
        if (this.options && this.options.apiEndpoint) {
            return this.options.apiEndpoint
        }

        // Use the dynamic API configuration
        return getApiEndpoint()
    }

    /**
     * Parse shared ID from URL parameters
     * Supports both query parameter (?share=id) and path parameter (/share/id)
     */
    static parseSharedId(url = window.location.href) {
        try {
            const urlObj = new URL(url)

            // Check query parameter first: ?share=shareId
            const shareParam = urlObj.searchParams.get('share')
            if (shareParam) {
                return shareParam.trim()
            }

            // Check path parameter: /share/shareId
            const pathParts = urlObj.pathname.split('/')
            const shareIndex = pathParts.indexOf('share')
            if (shareIndex !== -1 && shareIndex + 1 < pathParts.length) {
                const shareId = pathParts[shareIndex + 1]
                if (shareId) {
                    return shareId.trim()
                }
            }

            return null
        } catch (error) {
            console.warn('Error parsing URL for shared ID:', error)
            return null
        }
    }

    /**
     * Check if current URL contains a share parameter
     */
    static hasSharedId(url = window.location.href) {
        return URLParameterHandler.parseSharedId(url) !== null
    }

    /**
     * Load shared JSON from API using share ID
     */
    async loadSharedJSON(sharedId) {
        if (!sharedId) {
            throw new Error('Share ID is required')
        }

        if (this.isLoading) {
            throw new Error('Already loading shared JSON')
        }

        this.isLoading = true
        this.options.onLoadStart(sharedId)
        this.dispatchEvent('loadStart', { sharedId })

        try {
            const result = await this.fetchSharedJSON(sharedId)

            if (result.success) {
                this.options.onLoadSuccess(result)
                this.dispatchEvent('loadSuccess', result)
                return result
            } else {
                const error = new Error(result.error || 'Failed to load shared JSON')
                error.code = result.code
                throw error
            }

        } catch (error) {
            console.error('Error loading shared JSON:', error)
            this.options.onLoadError(error)
            this.dispatchEvent('loadError', { error: error.message, sharedId })
            throw error
        } finally {
            this.isLoading = false
            this.options.onLoadComplete(sharedId)
            this.dispatchEvent('loadComplete', { sharedId })
        }
    }

    /**
     * Fetch shared JSON data from API
     */
    async fetchSharedJSON(sharedId) {
        const url = `${this.options.apiEndpoint}/${encodeURIComponent(sharedId)}`

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                }
            })

            if (!response.ok) {
                // Handle specific HTTP status codes
                if (response.status === 404) {
                    return {
                        success: false,
                        error: 'Shared JSON not found or has expired',
                        code: 'NOT_FOUND'
                    }
                } else if (response.status === 410) {
                    return {
                        success: false,
                        error: 'Shared JSON has expired',
                        code: 'EXPIRED'
                    }
                } else {
                    const errorData = await response.json().catch(() => ({}))
                    return {
                        success: false,
                        error: errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`,
                        code: errorData.error?.code || 'HTTP_ERROR'
                    }
                }
            }

            const result = await response.json()
            return result

        } catch (error) {
            console.error('API request failed:', error)

            // Provide helpful error messages based on the error type
            let errorMessage = error.message || 'Failed to load shared JSON'
            let errorCode = 'NETWORK_ERROR'

            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                errorMessage = 'Unable to connect to the sharing service. Please check your internet connection.'
                errorCode = 'CONNECTION_ERROR'
            } else if (error.message.includes('CORS')) {
                errorMessage = 'Cross-origin request blocked. The API may need CORS configuration.'
                errorCode = 'CORS_ERROR'
            }

            return {
                success: false,
                error: errorMessage,
                code: errorCode
            }
        }
    }

    /**
     * Update browser URL without page reload
     * Useful for updating the URL after loading shared JSON
     */
    static updateURL(sharedId, replaceState = true) {
        if (!window.history || !window.location) return

        try {
            const url = new URL(window.location.href)

            if (sharedId) {
                // Add or update share parameter
                url.searchParams.set('share', sharedId)
            } else {
                // Remove share parameter
                url.searchParams.delete('share')
            }

            // Update URL without page reload
            if (replaceState) {
                window.history.replaceState(null, '', url.toString())
            } else {
                window.history.pushState(null, '', url.toString())
            }
        } catch (error) {
            console.warn('Error updating URL:', error)
        }
    }

    /**
     * Clear share parameter from URL
     */
    static clearShareParameter() {
        URLParameterHandler.updateURL(null)
    }

    /**
     * Generate share URL for given share ID
     */
    static generateShareURL(sharedId, baseUrl = window.location.origin) {
        if (!sharedId) return null

        try {
            const url = new URL(baseUrl)
            url.searchParams.set('share', sharedId)
            return url.toString()
        } catch (error) {
            console.warn('Error generating share URL:', error)
            return `${baseUrl}?share=${encodeURIComponent(sharedId)}`
        }
    }

    /**
     * Validate share ID format
     */
    static isValidShareId(shareId) {
        if (!shareId || typeof shareId !== 'string') return false

        // Basic validation: should be alphanumeric with possible hyphens/underscores
        // Adjust pattern based on your ID generation strategy
        const pattern = /^[a-zA-Z0-9_-]{8,64}$/
        return pattern.test(shareId.trim())
    }

    /**
     * Check if currently loading
     */
    getLoadingState() {
        return this.isLoading
    }

    /**
     * Update API endpoint
     */
    setApiEndpoint(endpoint) {
        this.options.apiEndpoint = endpoint
    }

    /**
     * Event system for component communication
     */
    dispatchEvent(type, detail = {}) {
        const event = new CustomEvent(`urlParameterHandler:${type}`, {
            detail: { ...detail, component: this },
            bubbles: true
        })
        document.dispatchEvent(event)
    }

    /**
     * Static method to initialize and handle shared JSON on app startup
     */
    static async initializeSharedJSON(options = {}) {
        const sharedId = URLParameterHandler.parseSharedId()

        if (!sharedId) {
            return null // No shared JSON to load
        }

        if (!URLParameterHandler.isValidShareId(sharedId)) {
            console.warn('Invalid share ID format:', sharedId)
            return null
        }

        const handler = new URLParameterHandler(options)

        try {
            const result = await handler.loadSharedJSON(sharedId)
            return {
                success: true,
                sharedId,
                data: result,
                handler
            }
        } catch (error) {
            return {
                success: false,
                sharedId,
                error: error.message,
                handler
            }
        }
    }
}